import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Cake, Plus, Pencil, Trash2, Search, X } from 'lucide-react';
import api from '../../services/api';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const formatBirthDate = (key) => {
  const [y, m, d] = key.split('-').map(Number);
  return `${String(d).padStart(2, '0')} ${MONTHS[m - 1]} ${y}`;
};

const todayInputValue = () => {
  const t = new Date();
  return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
};

const errorText = (err, fallback) =>
  err?.response?.data?.message || err?.response?.data?.errors?.[0]?.msg || fallback;

const BirthdayManagement = () => {
  const [birthdays, setBirthdays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState(null); // { mode: 'add'|'edit', id, name, employeeId, jobTitle, dob }
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null); // { type: 'success'|'error', text }

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/birthdays');
      setBirthdays(data.birthdays || []);
    } catch (err) {
      setNotice({ type: 'error', text: errorText(err, 'Failed to load birthdays') });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return birthdays;
    return birthdays.filter((b) =>
      `${b.name} ${b.employeeId || ''} ${b.jobTitle || ''}`.toLowerCase().includes(q));
  }, [birthdays, search]);

  const flash = (type, text) => {
    setNotice({ type, text });
    setTimeout(() => setNotice(null), 4000);
  };

  const openAdd = () => setModal({ mode: 'add', id: null, name: '', employeeId: '', jobTitle: '', dob: '' });
  const openEdit = (b) => setModal({ mode: 'edit', id: b.id, name: b.name, employeeId: b.employeeId || '', jobTitle: b.jobTitle || '', dob: b.birthDate });

  const save = async () => {
    if (modal.mode === 'add' && !modal.name.trim()) return flash('error', 'Please enter the employee name');
    if (!modal.dob) return flash('error', 'Please choose a date of birth');
    setSaving(true);
    try {
      if (modal.mode === 'add') {
        await api.post('/birthdays', {
          name: modal.name.trim(),
          employeeId: modal.employeeId.trim(),
          jobTitle: modal.jobTitle.trim(),
          dateOfBirth: modal.dob,
        });
        flash('success', 'Birthday added');
      } else {
        await api.put(`/birthdays/${modal.id}`, { dateOfBirth: modal.dob });
        flash('success', 'Birthday updated');
      }
      setModal(null);
      await load();
    } catch (err) {
      flash('error', errorText(err, 'Failed to save birthday'));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (b) => {
    if (!window.confirm(`Remove ${b.name} from the Birthday list?`)) return;
    try {
      await api.delete(`/birthdays/${b.id}`);
      flash('success', 'Birthday deleted');
      await load();
    } catch (err) {
      flash('error', errorText(err, 'Failed to delete birthday'));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br from-pink-500 to-purple-600 text-white shadow">
            <Cake className="h-5 w-5" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Birthday List</h2>
            <p className="text-sm text-gray-500">{birthdays.length} employee birthdays</p>
          </div>
        </div>
        <button
          onClick={openAdd}
          className="inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700"
        >
          <Plus className="h-4 w-4" /> Add Birthday
        </button>
      </div>

      {notice && (
        <div className={`rounded-xl border px-4 py-3 text-sm ${
          notice.type === 'success'
            ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
            : 'border-red-200 bg-red-50 text-red-700'}`}>
          {notice.text}
        </div>
      )}

      <div className="rounded-2xl border border-gray-200 bg-white shadow-sm">
        <div className="border-b border-gray-100 p-4">
          <div className="relative max-w-sm">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, ID or job title"
              className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-4 py-3">#</th>
                <th className="px-4 py-3">Employee</th>
                <th className="px-4 py-3">Employee ID</th>
                <th className="px-4 py-3">Job Title</th>
                <th className="px-4 py-3">Birthday</th>
                <th className="px-4 py-3">Next Birthday</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-gray-500">Loading…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-gray-500">No birthdays found</td></tr>
              ) : filtered.map((b, i) => (
                <tr key={b.id} className={b.daysUntil === 0 ? 'bg-pink-50' : 'hover:bg-gray-50'}>
                  <td className="px-4 py-3 text-gray-500">{i + 1}</td>
                  <td className="px-4 py-3 font-medium text-gray-900">{b.name}</td>
                  <td className="px-4 py-3 text-gray-600">{b.employeeId || '—'}</td>
                  <td className="px-4 py-3 text-gray-600">{b.jobTitle || '—'}</td>
                  <td className="px-4 py-3 text-gray-900">{formatBirthDate(b.birthDate)}</td>
                  <td className="px-4 py-3">
                    {b.daysUntil === 0 ? (
                      <span className="rounded-full bg-pink-100 px-2.5 py-1 text-xs font-semibold text-pink-700">Today 🎉</span>
                    ) : (
                      <span className="text-gray-600">in {b.daysUntil} day{b.daysUntil === 1 ? '' : 's'}</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-2">
                      <button onClick={() => openEdit(b)} title="Edit"
                        className="rounded-lg border border-gray-200 p-2 text-gray-600 hover:border-indigo-300 hover:text-indigo-700">
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button onClick={() => remove(b)} title="Delete"
                        className="rounded-lg border border-gray-200 p-2 text-gray-600 hover:border-red-300 hover:text-red-600">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">
                {modal.mode === 'add' ? 'Add Birthday' : 'Edit Birthday'}
              </h3>
              <button onClick={() => setModal(null)} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Employee Name</label>
                {modal.mode === 'add' ? (
                  <input
                    value={modal.name}
                    onChange={(e) => setModal({ ...modal, name: e.target.value })}
                    placeholder="Enter employee name"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none"
                  />
                ) : (
                  <input value={modal.name} disabled
                    className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-600" />
                )}
              </div>

              {modal.mode === 'add' && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">Employee ID <span className="text-gray-400">(optional)</span></label>
                    <input
                      value={modal.employeeId}
                      onChange={(e) => setModal({ ...modal, employeeId: e.target.value })}
                      placeholder="e.g. PFS0099"
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">Job Title <span className="text-gray-400">(optional)</span></label>
                    <input
                      value={modal.jobTitle}
                      onChange={(e) => setModal({ ...modal, jobTitle: e.target.value })}
                      placeholder="e.g. Associate"
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none"
                    />
                  </div>
                </div>
              )}

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Date of Birth</label>
                <input
                  type="date"
                  max={todayInputValue()}
                  value={modal.dob}
                  onChange={(e) => setModal({ ...modal, dob: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none"
                />
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => setModal(null)}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
                Cancel
              </button>
              <button onClick={save} disabled={saving}
                className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-60">
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BirthdayManagement;